import { BadRequestError, ForbiddenError, HttpError, ValidationFailuresResponse } from "components/ServerValidationErrors";
import { ProductsApiUrl } from "env-config";

import { acquireAccessToken } from "./access-token-service";

const baseApiUrl = (): string => ProductsApiUrl().replace(/[/]$/, "");

//#region Type definitions

export type SearchProductsOptions = {
    fieldCompanyId: number;
    sku?: string | undefined | null;
    description?: string | undefined | null;
    pageNumber: number;
    itemsPerPage: number;
};

export type SearchProductsItemResponse = {
    id: number;
    sku: string;
    description: string;
};

export type SearchProductsResponse = {
    pageNumber: number;
    itemsPerPage: number;
    totalCount: number;
    items: SearchProductsItemResponse[];
};

export type GetProductByIdResponse = {
    id: number;
    sku: string;
    description: string;
};

export type GetProductsBySkus = {
    fieldCompanyId: number,
    skus: string[]
}

export type GetProductsBySkusResponse = {
    id:number,
    fieldCompanyId: number,
    sku:string,
    description:string
}

export type ProductsWithLedgerManagedStatus = {
    ItemsToUpdate: ProductWithLedgerManagedStatus[],
    AlreadyManagedItems: ProductWithLedgerManagedStatus[],
    NotFoundItems: ProductWithLedgerManagedStatus[],
}

export type ProductWithLedgerManagedStatus = {
    productId: number | null,
    isLedgerManaged: boolean | null,
    description: string,
    sku: string
}


//#endregion

async function apiFetch<TResponse>(path: string, options: RequestInit): Promise<TResponse> {
    const url = `${baseApiUrl()}${path}`;
    const accessToken = await acquireAccessToken("api://product-catalogue-api", "access");
    const response = await fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${accessToken}` },
    });
    if (response.ok) {
        const result = await response.json();
        return result as TResponse;
    } else if (response.status === 400) {
        throw new BadRequestError(
            response.status,
            response.statusText,
            (await response.json()) as ValidationFailuresResponse
        );
    } else if (response.status === 403) {
        throw new ForbiddenError(response.status, await response.text());
    } else {
        throw new HttpError(response.status, await response.text());
    }
}

function toQuery(obj: any) {
    return Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .filter((k) => obj[k] !== null)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
        .join("&");
}

export async function searchProducts(options: SearchProductsOptions): Promise<SearchProductsResponse> {
    return await apiFetch<SearchProductsResponse>(`/products/search?${toQuery(options)}`, {
        headers: { "Content-Type": "application/json" },
    });
}

export async function getProductById(id: number): Promise<GetProductByIdResponse> {
    return await apiFetch<GetProductByIdResponse>(`/products/${id}`, {
        headers: { "Content-Type": "application/json" },
    });
}

export async function getProductsById(ids: number[]): Promise<GetProductByIdResponse[]> {
    const query = ids.map((id) => `Ids=${encodeURIComponent(id)}`).join("&");
    return await apiFetch<GetProductByIdResponse[]>(`/products?${query}`, {
        headers: { "Content-Type": "application/json" },
    });
}

export async function getProductsBySkus(data: GetProductsBySkus): Promise<GetProductsBySkusResponse[]> {
    let skus = data.skus.map((sku) => `Skus=${encodeURIComponent(sku.toLowerCase().trim())}`).join("&");
    return await apiFetch<GetProductsBySkusResponse[]>(`/products?fieldCompanyId=${data.fieldCompanyId}&${skus}`, {
        headers: { "Content-Type": "application/json" },
    });
}
